import { api } from "./api.js";
import { navigate } from "./app.js";
import { getCurrentUser } from "./auth.js";
import { esc, avatarHtml, displayName, timeAgo } from "./utils.js";

/**
 * The navbar bell: an unread badge, and a dropdown of the recent few.
 *
 * Polled rather than pushed. Real-time would mean websockets, which
 * gunicorn's sync workers do not do — a much larger change than a badge
 * justifies. Polling is gated on the tab being visible, so a backgrounded
 * tab costs nothing.
 */

const POLL_MS = 60_000;
const DROPDOWN_LIMIT = 8;
/** Past this the exact number stops being useful and starts being wide. */
const BADGE_MAX = 9;

let wrap = null;
let badge = null;
let list = null;
let readAllBtn = null;
let pollHandle = null;
let lastCount = 0;
// The bellBound guard below lives on the toggle element, which is
// enough for the button's own handlers — a fresh navbar means a fresh
// button. This listener is on `document`, which outlives any navbar, so
// it needs a guard that does too, or re-initialising would stack a
// second copy and poll twice on every tab switch.
let visibilityBound = false;

/** One sentence per kind. Shared with the full page so the two cannot
 *  describe the same event differently. */
export function notificationText(n) {
  const who = n.actor ? displayName(n.actor) : "Someone";
  switch (n.kind) {
    case "follow":
      return `${who} followed you`;
    case "reaction":
      return n.subject?.type === "comment"
        ? `${who} reacted to your comment`
        : `${who} reacted to your post`;
    case "comment":
      return `${who} commented on your post`;
    case "attending":
      return `${who} is coming to ${n.subject?.title ?? "your dive event"}`;
    case "cohort_invite":
      return `${who} invited you to join ${n.subject?.name ?? "a cohort"}`;
    default:
      // A kind this build does not know about is still worth showing —
      // an older client should not silently drop someone's inbox.
      return `${who} did something`;
  }
}

/** The second line: enough of the subject to tell which one is meant. */
function subtitle(n) {
  if (n.kind === "attending") return "";
  if (n.kind === "cohort_invite") return n.subject?.is_private ? "Private cohort · accept or decline on its page" : "Accept or decline on its page";
  return n.subject?.excerpt ?? "";
}

export function notificationRowHtml(n, { asDropdownItem = false } = {}) {
  const name = n.actor ? displayName(n.actor) : "Someone";
  const sub = subtitle(n);
  return `
    <button type="button"
            class="${asDropdownItem ? "dropdown-item" : "list-group-item list-group-item-action"} d-flex gap-2 align-items-start text-wrap py-2 ${n.read_at ? "" : "dc-unread"}"
            data-notification-id="${n.id}" data-go="${esc(n.target_path)}">
      ${avatarHtml(name, { sizePx: 32, fontSize: "0.75rem" })}
      <span class="flex-grow-1 small lh-sm">
        <span class="${n.read_at ? "" : "fw-semibold"}">${esc(notificationText(n))}</span>
        ${sub ? `<span class="d-block text-slate">${esc(sub)}</span>` : ""}
        <span class="d-block text-slate">${esc(timeAgo(n.created_at))}</span>
      </span>
    </button>`;
}

function setBadge(count) {
  lastCount = count;
  if (!badge) return;
  badge.classList.toggle("d-none", count === 0);
  badge.textContent = count > BADGE_MAX ? `${BADGE_MAX}+` : String(count);
  badge.setAttribute("aria-label", `${count} unread notifications`);
  readAllBtn?.classList.toggle("d-none", count === 0);
}

export async function refreshUnreadCount() {
  if (getCurrentUser() == null) {
    setBadge(0);
    return;
  }
  try {
    const { count } = await api.get("/notifications/unread-count");
    setBadge(count ?? 0);
  } catch (_) {
    // A failed poll is not worth telling anyone about; the next one in a
    // minute will either work or not. Leaving the badge as it was beats
    // blanking it and implying the inbox emptied.
  }
}

/** Marks one read locally and on the server, then goes to its subject. */
async function openNotification(id, path) {
  const row = list?.querySelector(`[data-notification-id="${id}"]`);
  const wasUnread = row?.classList.contains("dc-unread");
  if (wasUnread) {
    row.classList.remove("dc-unread");
    setBadge(Math.max(0, lastCount - 1));
  }
  navigate(path);
  if (wasUnread) {
    try {
      await api.post(`/notifications/${id}/read`);
    } catch (_) {
      // The badge is already down. Re-raising it here would be a worse
      // lie than a count that is briefly one too low, and the next poll
      // corrects it either way.
    }
  }
}

async function loadDropdown() {
  if (!list) return;
  list.innerHTML = `<div class="text-center py-3"><div class="spinner-border spinner-border-sm text-primary"></div></div>`;
  let items;
  try {
    ({ items } = await api.getPage("/notifications", { limit: DROPDOWN_LIMIT }));
  } catch (_) {
    list.innerHTML = `<div class="px-3 py-3 text-slate small">Could not load notifications.</div>`;
    return;
  }
  if (!items.length) {
    list.innerHTML = `<div class="px-3 py-3 text-slate small">Nothing yet. Follows, reactions, comments and sign-ups show up here.</div>`;
    return;
  }
  list.innerHTML = items.map((n) => notificationRowHtml(n, { asDropdownItem: true })).join("");
  list.querySelectorAll("[data-notification-id]").forEach((row) => {
    row.addEventListener("click", () =>
      openNotification(Number(row.dataset.notificationId), row.dataset.go)
    );
  });
}

export async function markAllRead() {
  try {
    await api.post("/notifications/read-all");
  } catch (_) {
    return;
  }
  setBadge(0);
  list?.querySelectorAll(".dc-unread").forEach((row) => {
    row.classList.remove("dc-unread");
    row.querySelector(".fw-semibold")?.classList.remove("fw-semibold");
  });
}

function startPolling() {
  stopPolling();
  // Only while the tab is in front. A backgrounded tab polling every
  // minute is a request per minute per tab that nobody will read.
  if (document.visibilityState !== "visible") return;
  pollHandle = setInterval(refreshUnreadCount, POLL_MS);
}

function stopPolling() {
  if (pollHandle != null) clearInterval(pollHandle);
  pollHandle = null;
}

/** Shows or hides the whole control, and starts or stops the poll.
 *  Called on every auth change. */
export function refreshBellVisibility() {
  if (!wrap) return;
  const signedIn = getCurrentUser() != null;
  wrap.classList.toggle("d-none", !signedIn);
  if (!signedIn) {
    stopPolling();
    setBadge(0);
    return;
  }
  refreshUnreadCount();
  startPolling();
}

export function initBell() {
  wrap = document.getElementById("nav-bell-wrap");
  if (!wrap) return;
  badge = document.getElementById("nav-bell-badge");
  list = document.getElementById("nav-bell-list");
  readAllBtn = document.getElementById("nav-bell-read-all");
  const toggle = document.getElementById("nav-bell");

  // Idempotent, as initSearchControl is: a second call would otherwise
  // stack handlers and double every click.
  if (toggle.dataset.bellBound === "true") {
    refreshBellVisibility();
    return;
  }
  toggle.dataset.bellBound = "true";

  // Fetch on open rather than keeping a list in sync in the background:
  // the badge is the thing that has to be current, and it is one small
  // count instead of a page of rows.
  //
  // Bootstrap's own event, not a click handler. Bootstrap opens the
  // dropdown *before* a listener on the button itself runs, so checking
  // aria-expanded there reads "true" on the way open and "false" on the
  // way closed — it loaded on dismiss and left the opened menu empty.
  // show.bs.dropdown fires on the container and does not depend on which
  // handler happens to run first.
  wrap.addEventListener("show.bs.dropdown", loadDropdown);
  readAllBtn?.addEventListener("click", markAllRead);
  document.getElementById("nav-bell-see-all")?.addEventListener("click", () => {
    navigate("/notifications");
  });

  if (!visibilityBound) {
    visibilityBound = true;
    bindVisibility();
  }

  refreshBellVisibility();
}

function bindVisibility() {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && getCurrentUser() != null) {
      // Catch up on whatever arrived while the tab was in the background,
      // then resume the timer.
      refreshUnreadCount();
      startPolling();
    } else {
      stopPolling();
    }
  });
}
